/**
 * election.js
 * ---------------------------------------------------------------------------
 * Vierte Schwesterdatei neben halloween.js, xmas.js und easter.js: identische
 * Logik, eigener Namensraum (el-).
 *
 * Erzeugt die Stimmzettel und Häkchen im Hintergrund und lässt sie umherziehen.
 * Nähert sich der Mauszeiger auf weniger als CONFIG.fleeRadius Pixel, stieben
 * sie auseinander und beruhigen sich danach wieder auf ihr Reisetempo.
 * Das Konfetti gehört nicht dazu, das läuft rein über CSS.
 *
 * Zusätzlich verschiebt eine leichte Parallaxe Mond und Stadt.
 * Kein Klick-Verhalten, keine Abhängigkeiten, kein Build.
 *
 * Startet nicht von selbst — einmal aufrufen:
 *
 *   import { initElections } from "./elections.js";
 *   initElections();
 *
 * Aktiviert wird jedes Element mit der Klasse .el-scene. Wird mindestens eine
 * gefunden, bekommt der body zusätzlich die Klasse .is-election.
 */
export function elections() {
    "use strict";

    var CONFIG = {
        ballots:     6,      // Anzahl Stimmzettel (Desktop, mobil halbiert)
        checks:      5,      // Anzahl Häkchen
        fleeRadius:  170,    // ab dieser Distanz zum Zeiger weichen sie aus (px)
        fleeForce:   1.1,    // Heftigkeit des Ausweichens
        maxSpeed:    3.6,    // Fluchttempo (px pro Frame bei 60 fps)
        ballotSpeed: 0.55,   // Reisetempo Stimmzettel — Papier segelt, es fliegt nicht
        checkSpeed:  0.3,    // Reisetempo Häkchen
        wander:      0.06,   // Stärke der zufälligen Richtungswechsel
        calmDown:    0.012,  // wie schnell der Schreck abklingt
        margin:      70,     // ab diesem Randabstand drehen sie ein
        ballotSize:  [18, 32],
        checkSize:   [14, 26]
    };

    var MARK_COLORS = ["#ffd08a", "#f6ecd8", "#ffe6b0", "#dfe7f2"];

    var BALLOT =
        '<span class="el-critter__body">' +
        '<i class="el-sheet el-sheet--l"></i>' +
        '<i class="el-sheet el-sheet--r"></i>' +
        '</span>';

    var CHECK = '<span class="el-critter__body"><i></i><i></i></span>';

    function reducedMotion() {
        return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    }

    function rand(min, max) { return min + Math.random() * (max - min); }
    function pick(list) { return list[Math.floor(Math.random() * list.length)]; }

    function create(scene) {
        var layer = scene.querySelector(".el-critters");
        if (!layer) return;

        var W = window.innerWidth;
        var H = window.innerHeight;
        var small = W < 700;
        var critters = [];
        var pointer = { x: -9999, y: -9999, active: false };
        var running = true;
        var last = 0;

        function spawn(kind, count) {
            var isBallot = kind === "ballot";
            var range = isBallot ? CONFIG.ballotSize : CONFIG.checkSize;

            for (var i = 0; i < count; i++) {
                var size = rand(range[0], range[1]);
                var el = document.createElement("span");
                el.className = "el-critter el-critter--" + kind;
                el.innerHTML = isBallot ? BALLOT : CHECK;
                el.style.setProperty("--el-size", size.toFixed(1) + "px");
                if (!isBallot) el.style.setProperty("--el-mark", pick(MARK_COLORS));
                el.style.opacity = isBallot ? rand(0.75, 1).toFixed(2) : rand(0.6, 0.95).toFixed(2);
                layer.appendChild(el);

                var dir = rand(0, Math.PI * 2);
                var base = (isBallot ? CONFIG.ballotSpeed : CONFIG.checkSpeed) * rand(0.75, 1.3);

                critters.push({
                    el: el,
                    kind: kind,
                    w: size,
                    h: isBallot ? size / 1.3 : size,
                    x: rand(0, W),
                    y: rand(0, H * 0.8),
                    vx: Math.cos(dir) * base,
                    vy: Math.sin(dir) * base,
                    base: base,
                    flee: 0,
                    face: 1,
                    fleeing: false
                });
            }
        }

        spawn("ballot", small ? Math.ceil(CONFIG.ballots / 2) : CONFIG.ballots);
        spawn("check", small ? Math.ceil(CONFIG.checks / 2) : CONFIG.checks);

        function step(now) {
            if (!running) return;
            var dt = last ? Math.min((now - last) / 16.667, 3) : 1;
            last = now;

            for (var i = 0; i < critters.length; i++) {
                var c = critters[i];

                c.vx += rand(-1, 1) * CONFIG.wander * dt;
                c.vy += rand(-1, 1) * CONFIG.wander * dt;

                if (pointer.active) {
                    var dx = c.x - pointer.x;
                    var dy = c.y - pointer.y;
                    var d = Math.sqrt(dx * dx + dy * dy);
                    if (d < CONFIG.fleeRadius) {
                        var push = 1 - d / CONFIG.fleeRadius;
                        var inv = 1 / (d || 1);
                        c.vx += dx * inv * CONFIG.fleeForce * push * dt;
                        c.vy += dy * inv * CONFIG.fleeForce * push * dt;
                        c.flee = 1;
                    }
                }
                c.flee = Math.max(0, c.flee - CONFIG.calmDown * dt);

                if (c.x < CONFIG.margin)          c.vx += 0.07 * dt;
                else if (c.x > W - CONFIG.margin) c.vx -= 0.07 * dt;
                if (c.y < CONFIG.margin)          c.vy += 0.07 * dt;
                else if (c.y > H - CONFIG.margin) c.vy -= 0.07 * dt;

                var speed = Math.sqrt(c.vx * c.vx + c.vy * c.vy) || 0.0001;
                var target = c.base + (CONFIG.maxSpeed - c.base) * c.flee;
                var next = speed + (target - speed) * 0.05 * dt;
                c.vx = c.vx / speed * next;
                c.vy = c.vy / speed * next;

                c.x += c.vx * dt;
                c.y += c.vy * dt;
                if (c.x < -c.w) c.x = W + c.w;
                else if (c.x > W + c.w) c.x = -c.w;
                if (c.y < -c.h) c.y = H + c.h;
                else if (c.y > H + c.h) c.y = -c.h;

                c.el.style.transform =
                    "translate3d(" + (c.x - c.w / 2).toFixed(1) + "px," +
                    (c.y - c.h / 2).toFixed(1) + "px,0)";

                if (c.kind === "ballot") {
                    var face = c.vx < 0 ? -1 : 1;
                    if (face !== c.face) {
                        c.face = face;
                        c.el.style.setProperty("--el-face", face);
                    }
                }

                var fleeing = c.flee > 0.2;
                if (fleeing !== c.fleeing) {
                    c.fleeing = fleeing;
                    c.el.classList.toggle("is-fleeing", fleeing);
                }
            }

            requestAnimationFrame(step);
        }

        window.addEventListener("pointermove", function (e) {
            pointer.x = e.clientX;
            pointer.y = e.clientY;
            pointer.active = true;
        }, { passive: true });

        document.addEventListener("pointerleave", function () {
            pointer.active = false;
        });

        window.addEventListener("resize", function () {
            W = window.innerWidth;
            H = window.innerHeight;
        }, { passive: true });

        document.addEventListener("visibilitychange", function () {
            if (document.hidden) {
                running = false;
            } else if (!running) {
                running = true;
                last = 0;
                requestAnimationFrame(step);
            }
        });

        requestAnimationFrame(step);
    }

    function init(scene) {
        if (scene.dataset.elReady) return;
        scene.dataset.elReady = "1";

        window.addEventListener("pointermove", function (e) {
            scene.style.setProperty("--el-px", (e.clientX / window.innerWidth * 2 - 1).toFixed(3));
            scene.style.setProperty("--el-py", (e.clientY / window.innerHeight * 2 - 1).toFixed(3));
        }, { passive: true });

        if (!reducedMotion()) create(scene);
    }

    function boot() {
        var scenes = document.querySelectorAll(".el-scene");
        if (!scenes.length) return;

        document.body.classList.add("is-elections");

        Array.prototype.forEach.call(scenes, init);
    }

    if (document.readyState === "loading") {
        document.addEventListener("DOMContentLoaded", boot);
    } else {
        boot();
    }
}